import * as Phaser from "phaser"
import { GameState } from "../core/game-state"
import { InputManager } from "../core/input-mapper"
import { SCENE, TEX } from "../core/keys"
import { THEME } from "../core/theme"
import { playConfirm, playHover } from "../core/ui-sfx"
import { GAME_HEIGHT, GAME_WIDTH } from "../data/constants"
import { PortalManager } from "../systems/portal-manager"
import { createShopOffers, getShopRerollPrice } from "../systems/shop-manager"
import type { Player, ShopOffer, Upgrade } from "../types"
import {
  type FilterableGameObject,
  NEON,
  addAmbientNeonGlow,
  applyGlowFilter,
  applyModalCameraFx,
  clearCameraFx,
  drawCornerAccents,
  drawScanlines,
  neonTextShadow,
} from "../ui/neon-fx"
import { applyUpgrade } from "../utils"
import { getWaveRuntime } from "./wave-runtime-api"

const FONT = THEME.fonts
const COLOR = THEME.colors

// Gradius style bar layout
const BAR_LAYOUT = {
  y: GAME_HEIGHT - 92,
  itemW: 190,
  btnW: 115,
  itemH: 70,
  gap: 15,
}

interface ShopSceneData {
  wave: number
  coins: number
  coinsThisWave: number
  player: Player
}

export class ShopScene extends Phaser.Scene {
  private wave = 1
  private coins = 0
  private player!: Player
  private offers: ShopOffer[] = []
  private boughtIds: Set<string> = new Set()
  private boughtSlots: Set<string> = new Set()
  private lockedOffers: ShopOffer[] = []
  private rerollCount = 0
  private uiContainer!: Phaser.GameObjects.Container
  private waveCamera?: Phaser.Cameras.Scene2D.Camera
  private inputManager!: InputManager

  // New state for arcade style
  private selectedIndex = 0

  constructor() {
    super({ key: SCENE.SHOP })
  }

  init(data: ShopSceneData): void {
    this.wave = data.wave ?? 1
    this.coins = data.coins ?? GameState.instance.coins
    const player = data.player ?? GameState.instance.player
    if (!player) {
      throw new Error("ShopScene requires player data")
    }
    this.player = player
    this.boughtIds = new Set()
    this.boughtSlots = new Set()
    this.lockedOffers = []
    this.rerollCount = 0
    this.selectedIndex = 0
  }

  create(): void {
    this.inputManager = new InputManager(this, {
      onDown: (action, event) => {
        // Ignore global pointer events for these actions in the menu
        if (event?.pointer && (action === "CONFIRM" || action === "FLAP"))
          return

        switch (action) {
          case "LEFT":
            this.moveSelection(-1)
            break
          case "RIGHT":
            this.moveSelection(1)
            break
          case "CONFIRM":
          case "FLAP":
            this.confirmSelection()
            break
          case "REROLL":
            this.handleReroll()
            break
          case "BACK":
          case "PAUSE":
            this.finishShopping()
            break
        }
      },
    })

    if (this.scene.isActive(SCENE.WAVE) || this.scene.isPaused(SCENE.WAVE)) {
      this.waveCamera = this.scene.get(SCENE.WAVE).cameras.main
      applyModalCameraFx(this.waveCamera, {
        blurStrength: 1.5,
        blurSteps: 5,
        vignetteRadius: 0.5,
        vignetteStrength: 0.6,
      })
    }

    this.refreshOffers()
    this.uiContainer = this.add.container(0, 0)

    this.renderHangar()

    this.events.once("shutdown", () => {
      this.tweens.killAll()
      this.inputManager.destroy()
      if (this.waveCamera) {
        clearCameraFx(this.waveCamera)
        this.waveCamera = undefined
      }
    })
  }

  private moveSelection(dir: number): void {
    const oldIndex = this.selectedIndex
    // 0, 1, 2 are offers, 3 is Reroll, 4 is Continue
    const totalOptions = this.offers.length + 2
    this.selectedIndex =
      (this.selectedIndex + dir + totalOptions) % totalOptions

    if (oldIndex !== this.selectedIndex) {
      playHover(this)
      this.renderHangar()
    }
  }

  private confirmSelection(): void {
    if (this.selectedIndex < this.offers.length) {
      const offer = this.offers[this.selectedIndex]
      if (!this.boughtSlots.has(offer.slotId) && this.coins >= offer.price) {
        playConfirm(this)
        this.handleBuy(offer)
      }
    } else if (this.selectedIndex === this.offers.length) {
      if (this.canReroll) {
        this.handleReroll()
      }
    } else {
      this.finishShopping()
    }
  }

  private get rerollPrice(): number {
    return getShopRerollPrice(this.wave, this.rerollCount)
  }

  private get canReroll(): boolean {
    // Standard shop allows multiple rerolls, cost increases via getShopRerollPrice
    return this.rerollCount < 5
  }

  private get rerollLabel(): string {
    if (!this.canReroll) {
      return "USED"
    }
    const freeRerolls = this.player.rerollCharges || 0
    if (freeRerolls > 0) {
      return `FREE (${freeRerolls})`
    }
    return `$ ${this.rerollPrice}`
  }

  private refreshOffers(): void {
    this.offers = createShopOffers({
      wave: this.wave,
      player: this.player,
      lockedOffers: this.lockedOffers,
      blockedUpgradeIds: [...this.boughtIds],
    })
  }

  private snapshotLockedOffers(): void {
    this.lockedOffers = this.offers.filter((o) => o.locked)
  }

  private persistState(includePlayer = false): void {
    const gs = GameState.instance
    gs.coins = this.coins
    if (includePlayer) {
      gs.player = this.player
    }
  }

  private addFittedText(
    x: number,
    y: number,
    text: string,
    config: Phaser.Types.GameObjects.Text.TextStyle,
    maxWidth: number,
    maxHeight: number,
  ): Phaser.GameObjects.Text {
    const resolved = {
      ...config,
      wordWrap: { width: maxWidth },
      align: "center",
    }
    const label = this.add.text(x, y, text, resolved).setOrigin(0.5)
    const baseFontSize =
      Number.parseInt(String(config.fontSize ?? "10"), 10) || 10

    for (let size = baseFontSize; size >= 7; size -= 1) {
      label.setFontSize(size)
      label.setWordWrapWidth(maxWidth, true)
      if (label.width <= maxWidth && label.height <= maxHeight) {
        break
      }
    }

    return label
  }

  private renderHangar(): void {
    this.tweens.killAll()
    this.uiContainer.removeAll(true)

    this.renderBackground()
    this.renderHeader()
    this.renderShipHologram()
    this.renderTacticalData()
    this.renderPowerBar()
    this.renderInventory()
  }

  private renderBackground(): void {
    // Solid base
    this.uiContainer.add(
      this.add.rectangle(
        GAME_WIDTH / 2,
        GAME_HEIGHT / 2,
        GAME_WIDTH,
        GAME_HEIGHT,
        COLOR.nearBlack,
        1,
      ),
    )

    // Tech Grid
    const gridGfx = this.add.graphics()
    gridGfx.lineStyle(1, NEON.cyan, 0.1)
    for (let x = 0; x < GAME_WIDTH; x += 40) {
      gridGfx.lineBetween(x, 0, x, GAME_HEIGHT)
    }
    for (let y = 0; y < GAME_HEIGHT; y += 40) {
      gridGfx.lineBetween(0, y, GAME_WIDTH, y)
    }
    this.uiContainer.add(gridGfx)

    // Glows
    this.uiContainer.add(
      addAmbientNeonGlow(this, 100, 100, NEON.blue, 300, 0.04),
    )
    this.uiContainer.add(
      addAmbientNeonGlow(
        this,
        GAME_WIDTH - 100,
        GAME_HEIGHT - 100,
        NEON.magenta,
        300,
        0.04,
      ),
    )

    // Scanlines
    this.uiContainer.add(
      drawScanlines(this, 0, 0, GAME_WIDTH, GAME_HEIGHT, 0.05, 2),
    )

    // Hangar decorative brackets
    const brackets = this.add.graphics()
    drawCornerAccents(
      brackets,
      20,
      20,
      GAME_WIDTH - 40,
      GAME_HEIGHT - 40,
      NEON.cyan,
      40,
      0.3,
    )
    this.uiContainer.add(brackets)
  }

  private renderHeader(): void {
    // Top Bar
    const headerH = 60
    const headerBg = this.add.graphics()
    headerBg.fillStyle(COLOR.black, 0.8)
    headerBg.fillRect(0, 0, GAME_WIDTH, headerH)
    headerBg.lineStyle(2, NEON.cyan, 0.4)
    headerBg.lineBetween(0, headerH, GAME_WIDTH, headerH)
    this.uiContainer.add(headerBg)

    const titleText = this.add.text(25, 20, "NOT A PET SHOP", {
      fontSize: "18px",
      fontFamily: FONT.pixel,
      color: COLOR.cyanBrightText,
      shadow: neonTextShadow(THEME.shadows.cyan, 8),
    })
    this.uiContainer.add(titleText)

    // Credits - Right aligned area
    const coinX = GAME_WIDTH - 400
    const coinIcon = this.add
      .sprite(coinX, 30, TEX.COIN_RED)
      .setDisplaySize(64, 64)
    coinIcon.play("anim_coin_m")
    this.uiContainer.add(coinIcon)

    this.uiContainer.add(
      this.add.text(coinX + 26, 20, `${this.coins}`, {
        fontSize: "16px",
        fontFamily: FONT.pixel,
        color: COLOR.warningText,
        shadow: neonTextShadow(THEME.shadows.gold, 6),
      }),
    )

    // Mission Info
    this.uiContainer.add(
      this.add
        .text(GAME_WIDTH - 150, 20, `SECTOR: ${this.wave}`, {
          fontSize: "14px",
          fontFamily: FONT.pixel,
          color: COLOR.whiteText,
        })
        .setOrigin(1, 0),
    )
  }

  private renderPortalExit(): void {
    const portalX = GAME_WIDTH - 40
    const portalY = 30
    const cost = 5
    const canAfford = this.coins >= cost

    const portalContainer = this.add.container(portalX, portalY)
    portalContainer.setDepth(10)

    const glow = this.add.graphics()
    const swirl = this.add.graphics()
    const portalRadius = 15

    const drawPortal = (time: number) => {
      glow.clear()
      swirl.clear()

      const pulseScale = 1 + Math.sin(time * 0.003) * 0.1

      glow.fillStyle(0x00ffaa, 0.15 * pulseScale)
      glow.fillCircle(0, 0, portalRadius * 1.8 * pulseScale)
      glow.fillStyle(0x00ffaa, 0.25 * pulseScale)
      glow.fillCircle(0, 0, portalRadius * 1.4 * pulseScale)
      glow.fillStyle(0x00dd88, 0.4 * pulseScale)
      glow.fillCircle(0, 0, portalRadius * pulseScale)

      const segments = 6
      for (let i = 0; i < segments; i++) {
        const angle = (i / segments) * Math.PI * 2 + time * 0.002
        const x1 = Math.cos(angle) * portalRadius * 0.3
        const y1 = Math.sin(angle) * portalRadius * 0.3
        const x2 = Math.cos(angle) * portalRadius * 0.9 * pulseScale
        const y2 = Math.sin(angle) * portalRadius * 0.9 * pulseScale
        swirl.lineStyle(2, 0xaaffcc, 0.8)
        swirl.beginPath()
        swirl.moveTo(x1, y1)
        swirl.lineTo(x2, y2)
        swirl.strokePath()
      }

      swirl.lineStyle(1, 0xffffff, 0.6)
      swirl.strokeCircle(0, 0, portalRadius * 0.4)
      swirl.lineStyle(2, 0x00ffaa, 0.9)
      swirl.strokeCircle(0, 0, portalRadius * 0.7 * pulseScale)
      swirl.lineStyle(1, 0x00dd88, 0.7)
      swirl.strokeCircle(0, 0, portalRadius)
    }

    drawPortal(0)

    const hitArea = this.add.circle(0, 0, portalRadius * 1.5)
    hitArea.setInteractive("pointerdown")
    hitArea.setAlpha(0)
    hitArea.setDepth(11)

    portalContainer.add([glow, swirl, hitArea])

    if (!canAfford) {
      glow.setAlpha(0.4)
      swirl.setAlpha(0.4)
    }

    const portalLabel = this.add
      .text(portalX - 25, portalY, "PORTAL", {
        fontSize: "10px",
        fontFamily: FONT.pixel,
        color: canAfford ? COLOR.warningText : COLOR.dangerText,
      })
      .setOrigin(1, 0.5)
    this.uiContainer.add(portalLabel)

    let portalTime = 0
    const portalTimer = this.time.addEvent({
      delay: 16,
      callback: () => {
        portalTime += 16
        drawPortal(portalTime)
      },
      loop: true,
    })

    hitArea.on("pointerover", () => {
      if (canAfford) {
        glow.setAlpha(1)
        swirl.setAlpha(1)
      }
      this.tweens.add({
        targets: portalContainer,
        scale: 1.2,
        duration: 200,
        ease: "Back.easeOut",
      })
    })

    hitArea.on("pointerout", () => {
      if (!canAfford) {
        glow.setAlpha(0.4)
        swirl.setAlpha(0.4)
      }
      this.tweens.add({
        targets: portalContainer,
        scale: 1.0,
        duration: 200,
        ease: "Back.easeOut",
      })
    })

    hitArea.on("pointerdown", () => {
      if (canAfford) {
        portalTimer.remove()
        playConfirm(this)
        this.coins -= cost
        this.persistState()

        const url = PortalManager.instance.getExitPortalUrl({
          speed: this.player.stats.attackSpeed,
        })
        window.location.href = url
      }
    })
  }

  private renderShipHologram(): void {
    const centerX = GAME_WIDTH * 0.44
    const centerY = GAME_HEIGHT * 0.44

    // Hologram Platform
    const platform = this.add.graphics()
    platform.lineStyle(2, NEON.cyan, 0.2)
    platform.strokeEllipse(centerX, centerY + 80, 120, 30)
    this.uiContainer.add(platform)

    // Hero portrait hologram
    const heroId = GameState.instance.character?.id ?? "bella"
    const ship = this.add.image(centerX, centerY, `portrait_${heroId}`)
    ship.setDisplaySize(160, 160)
    ship.setTint(NEON.cyan)
    ship.setAlpha(0.6)
    this.uiContainer.add(ship)

    applyGlowFilter(ship as unknown as FilterableGameObject, {
      color: NEON.cyan,
      outerStrength: 8,
      quality: 10,
    })

    // Floating animation
    this.tweens.add({
      targets: ship,
      y: centerY - 10,
      duration: 2000,
      yoyo: true,
      repeat: -1,
      ease: "Sine.easeInOut",
    })

    // Brackets around ship
    const brackets = this.add.graphics()
    drawCornerAccents(
      brackets,
      centerX - 120,
      centerY - 100,
      240,
      200,
      NEON.cyan,
      20,
      0.2,
    )
    this.uiContainer.add(brackets)

    // Status Labels
    this.uiContainer.add(
      this.add
        .text(centerX, centerY - 130, "PROTOTYPE-V1 STATUS: ACTIVE", {
          fontSize: "10px",
          fontFamily: FONT.pixel,
          color: COLOR.cyanSoftText,
        })
        .setOrigin(0.5),
    )
  }

  private renderTacticalData(): void {
    const panelX = GAME_WIDTH * 0.65
    const panelY = 80
    const panelW = 300
    const panelH = 340

    const bg = this.add.graphics()
    bg.fillStyle(COLOR.black, 0.6)
    bg.fillRect(panelX, panelY, panelW, panelH)
    drawCornerAccents(bg, panelX, panelY, panelW, panelH, NEON.blue, 15, 0.5)
    this.uiContainer.add(bg)

    this.uiContainer.add(
      this.add.text(panelX + 20, panelY + 20, "TACTICAL DATA", {
        fontSize: "14px",
        fontFamily: FONT.pixel,
        color: COLOR.infoText,
      }),
    )

    // If something is selected, show its data
    if (this.selectedIndex < this.offers.length) {
      const offer = this.offers[this.selectedIndex]
      const upgrade = offer.upgrade

      this.uiContainer.add(
        this.addFittedText(
          panelX + panelW / 2,
          panelY + 58,
          upgrade.name.toUpperCase(),
          {
            fontSize: "12px",
            fontFamily: FONT.pixel,
            color: COLOR.whiteText,
          },
          panelW - 40,
          32,
        ),
      )

      this.uiContainer.add(
        this.add.text(panelX + 20, panelY + 80, upgrade.description, {
          fontSize: "9px",
          fontFamily: FONT.pixel,
          color: COLOR.mutedText,
          wordWrap: { width: panelW - 40 },
          lineSpacing: 6,
        }),
      )

      // Stats
      let statY = panelY + 176
      const detailLines = this.getUpgradeDetailLines(upgrade)
      for (const line of detailLines) {
        this.uiContainer.add(
          this.addFittedText(
            panelX + panelW / 2,
            statY,
            `> ${line.text}`,
            {
              fontSize: "10px",
              fontFamily: FONT.pixel,
              color: line.color,
            },
            panelW - 40,
            18,
          ),
        )
        statY += 28
      }

      // Price
      const isBought = this.boughtSlots.has(offer.slotId)
      const canBuy = this.coins >= offer.price
      const priceColor = isBought
        ? COLOR.successText
        : canBuy
          ? COLOR.warningText
          : COLOR.dangerText
      const priceText = isBought ? "ACQUIRED" : `COST: $ ${offer.price}`

      this.uiContainer.add(
        this.add.text(panelX + 20, panelY + panelH - 40, priceText, {
          fontSize: "14px",
          fontFamily: FONT.pixel,
          color: priceColor,
        }),
      )
    } else if (this.selectedIndex === this.offers.length) {
      // Reroll info
      this.uiContainer.add(
        this.add.text(panelX + 20, panelY + 50, "REFRESH OFFERS", {
          fontSize: "12px",
          fontFamily: FONT.pixel,
          color: COLOR.whiteText,
        }),
      )
      this.uiContainer.add(
        this.add.text(
          panelX + 20,
          panelY + 80,
          "REROLL SECTOR FOR AVAILABLE TECHNOLOGY PACKAGES.",
          {
            fontSize: "9px",
            fontFamily: FONT.pixel,
            color: COLOR.mutedText,
            wordWrap: { width: panelW - 40 },
            lineSpacing: 8,
          },
        ),
      )
      this.uiContainer.add(
        this.add.text(
          panelX + 20,
          panelY + panelH - 40,
          `COST: $ ${this.rerollPrice}`,
          {
            fontSize: "14px",
            fontFamily: FONT.pixel,
            color: COLOR.warningText,
          },
        ),
      )
    } else {
      // Continue info
      this.uiContainer.add(
        this.add.text(panelX + 20, panelY + 50, "ENGAGE MISSION", {
          fontSize: "12px",
          fontFamily: FONT.pixel,
          color: COLOR.whiteText,
        }),
      )
      this.uiContainer.add(
        this.add.text(
          panelX + 20,
          panelY + 80,
          "PROCEED TO NEXT SECTOR. ALL SYSTEMS NOMINAL.",
          {
            fontSize: "9px",
            fontFamily: FONT.pixel,
            color: COLOR.mutedText,
            wordWrap: { width: panelW - 40 },
            lineSpacing: 8,
          },
        ),
      )
    }
  }

  private renderPowerBar(): void {
    const barY = BAR_LAYOUT.y
    const totalOffers = this.offers.length

    // Calculate total width to center
    const totalWidth =
      totalOffers * BAR_LAYOUT.itemW +
      2 * BAR_LAYOUT.btnW +
      (totalOffers + 1) * BAR_LAYOUT.gap
    let currentX = (GAME_WIDTH - totalWidth) / 2

    // 0 to N-1: Offers
    // N: Reroll
    // N+1: Continue

    const renderOption = (
      index: number,
      label: string,
      x: number,
      w: number,
      sublabel?: string,
      canAfford = true,
    ) => {
      const isSelected = this.selectedIndex === index
      const bg = this.add.graphics()

      const isEngage = label === "ENGAGE"
      // Unaffordable items get a dark brownish-red base; bought items keep blue
      const baseColor = isEngage
        ? 0x6f5618
        : isSelected
          ? canAfford
            ? 0x2f6f3b
            : 0x3a3a3a
          : canAfford
            ? 0x314a92
            : 0x252525
      const fillColor = Phaser.Display.Color.ValueToColor(baseColor)
      const stripeHeight = BAR_LAYOUT.itemH / 4
      for (let i = 0; i < 4; i++) {
        const stripeColor = fillColor.clone()
        stripeColor.darken(i * 10)
        bg.fillStyle(stripeColor.color, isSelected ? 1 : canAfford ? 0.9 : 0.75)
        bg.fillRect(x, barY + i * stripeHeight, w, stripeHeight)
      }
      bg.lineStyle(1, 0x000000, 1)
      bg.strokeRect(x, barY, w, BAR_LAYOUT.itemH)
      bg.lineStyle(1, fillColor.clone().lighten(28).color, 1)
      bg.lineBetween(x + 1, barY + 1, x + w - 2, barY + 1)
      bg.lineBetween(x + 1, barY + 1, x + 1, barY + BAR_LAYOUT.itemH - 2)
      bg.lineStyle(1, fillColor.clone().darken(38).color, 1)
      bg.lineBetween(
        x + w - 2,
        barY + 1,
        x + w - 2,
        barY + BAR_LAYOUT.itemH - 2,
      )
      bg.lineBetween(
        x + 1,
        barY + BAR_LAYOUT.itemH - 2,
        x + w - 2,
        barY + BAR_LAYOUT.itemH - 2,
      )
      this.uiContainer.add(bg)

      if (isSelected) {
        applyGlowFilter(bg as unknown as FilterableGameObject, {
          color: fillColor.color,
          outerStrength: 3,
        })
      }

      const labelColor = isSelected
        ? canAfford
          ? COLOR.whiteText
          : COLOR.mutedText
        : isEngage
          ? COLOR.warningText
          : canAfford
            ? COLOR.whiteTextDim
            : COLOR.dimText

      this.uiContainer.add(
        this.addFittedText(
          x + w / 2,
          barY + (sublabel ? 24 : 35),
          label,
          {
            fontSize: "9px",
            fontFamily: FONT.pixel,
            color: labelColor,
          },
          w - 18,
          sublabel ? 28 : 38,
        ),
      )

      if (sublabel) {
        const sublabelColor = isSelected
          ? canAfford
            ? COLOR.warningText
            : COLOR.mutedText
          : canAfford
            ? COLOR.dimText
            : COLOR.dimText
        this.uiContainer.add(
          this.addFittedText(
            x + w / 2,
            barY + 50,
            sublabel,
            {
              fontSize: "8px",
              fontFamily: FONT.pixel,
              color: sublabelColor,
            },
            w - 18,
            18,
          ),
        )
      }

      // Interaction zone
      const zone = this.add
        .zone(x + w / 2, barY + BAR_LAYOUT.itemH / 2, w, BAR_LAYOUT.itemH)
        .setInteractive({ useHandCursor: true })
      this.uiContainer.add(zone)
      this.inputManager.bindPointer(zone, {
        pointerover: () => {
          if (this.selectedIndex !== index) {
            this.selectedIndex = index
            playHover(this)
            this.renderHangar()
          }
        },
        pointerdown: () => {
          if (this.selectedIndex !== index) {
            this.selectedIndex = index
            playHover(this)
            this.renderHangar()
          } else {
            this.confirmSelection()
          }
        },
      })
    }

    // Offers
    this.offers.forEach((offer, i) => {
      const isBought = this.boughtSlots.has(offer.slotId)
      const canAfford = isBought || this.coins >= offer.price
      const label = isBought ? "ACQUIRED" : offer.upgrade.name.toUpperCase()
      const priceLabel = isBought ? "" : `$ ${offer.price}`
      renderOption(i, label, currentX, BAR_LAYOUT.itemW, priceLabel, canAfford)
      currentX += BAR_LAYOUT.itemW + BAR_LAYOUT.gap
    })

    // Reroll
    renderOption(
      totalOffers,
      "REROLL",
      currentX,
      BAR_LAYOUT.btnW,
      this.rerollLabel,
    )
    currentX += BAR_LAYOUT.btnW + BAR_LAYOUT.gap

    // Continue
    renderOption(totalOffers + 1, "ENGAGE", currentX, BAR_LAYOUT.btnW)
  }

  private renderInventory(): void {
    // Show current stats briefly on the left
    const startX = 40
    const startY = 80

    this.uiContainer.add(
      this.add.text(startX, startY, "SYS REPORT", {
        fontSize: "12px",
        fontFamily: FONT.pixel,
        color: COLOR.cyanSoftText,
      }),
    )

    const stats = [
      { label: "HP", val: `${Math.ceil(this.player.hp)}/${this.player.maxHp}` },
      {
        label: "SPD",
        val: `${Math.round(this.player.stats.attackSpeed * 100)}%`,
      },
    ]

    stats.forEach((s, i) => {
      this.uiContainer.add(
        this.add.text(startX, startY + 30 + i * 25, `${s.label}: ${s.val}`, {
          fontSize: "10px",
          fontFamily: FONT.pixel,
          color: COLOR.whiteText,
        }),
      )
    })
  }

  private getUpgradeDetailLines(
    upgrade: Upgrade,
  ): Array<{ text: string; color: string }> {
    const lines: Array<{ text: string; color: string }> = []

    if (upgrade.type === "stat" && upgrade.statBoost) {
      for (const [key, value] of Object.entries(upgrade.statBoost)) {
        const v = value as number
        if (v === 0) continue
        const isPositive = v > 0
        lines.push({
          text: `${isPositive ? "+" : ""}${this.formatStatValue(key, v)} ${this.statKeyLabel(key)}`,
          color: isPositive ? COLOR.successText : COLOR.dangerText,
        })
      }
    }

    if (upgrade.type === "item") {
      const item = upgrade
      if (item.healAmount) {
        lines.push({ text: `+${item.healAmount} HP`, color: COLOR.successText })
      }
      if (item.healFraction) {
        lines.push({
          text: `+${Math.round(item.healFraction * 100)}% HP`,
          color: COLOR.successText,
        })
      }

      if (item.passiveEffect) {
        for (const [key, value] of Object.entries(item.passiveEffect)) {
          const v = value as number
          if (v === 0) continue
          const isPositive = !key.includes("Penalty") ? v > 0 : v < 0
          const formatted =
            Math.abs(v) < 1
              ? `${Math.round(Math.abs(v) * 100)}%`
              : `${Math.abs(v)}`
          lines.push({
            text: `${isPositive ? "+" : "-"}${formatted} ${this.passiveKeyLabel(key)}`,
            color: isPositive ? COLOR.successText : COLOR.dangerText,
          })
        }
      }
    }

    return lines
  }

  private statKeyLabel(key: string): string {
    const map: Record<string, string> = {
      maxHp: "MAX HP",
      damage: "DAMAGE",
      attackSpeed: "SPEED",
      projectileSpeed: "PROJ SPD",
      critChance: "CRIT %",
      critDamage: "CRIT DMG",
      expGain: "XP GAIN",
      luck: "LUCK",
      magnetRange: "MAGNET",
    }
    return map[key] ?? key.toUpperCase()
  }

  private passiveKeyLabel(key: string): string {
    const map: Record<string, string> = {
      waveGuardPerWave: "GUARD",
      endWaveMaxHpGain: "HP/WAVE",
      endWaveAttackSpeedGain: "SPD/WAVE",
      comboDecayGracePeriod: "GRACE",
      combFillSpeedMultiplier: "COMBO FILL",
      bumperBounceForceMultiplier: "BOUNCE",
      contactDamageReduction: "RESIST",
      rerollCharges: "REROLL",
      damageMultiplier: "DAMAGE",
      hpMultiplier: "MAX HP",
      speedMultiplier: "BALL SPEED",
      wavesSkipped: "WAVES",
      coinMultiplier: "COINS",
      expMultiplier: "EXP",
      scoreMultiplier: "SCORE",
    }
    return map[key] ?? key.toUpperCase()
  }

  private formatStatValue(key: string, value: number): string {
    if (
      [
        "damage",
        "attackSpeed",
        "projectileSpeed",
        "critChance",
        "critDamage",
        "expGain",
      ].includes(key)
    ) {
      return `${Math.round(value * 100)}%`
    }
    return `${value}`
  }

  private handleReroll(): void {
    if (!this.canReroll) return
    const cost = this.rerollPrice

    // Check for free rerolls from Dice Bag / Lucky Charm
    const freeRerollsAvailable = this.player.rerollCharges || 0

    if (freeRerollsAvailable > 0) {
      // Use a free reroll
      this.player.rerollCharges -= 1
    } else {
      // Pay coins
      if (this.coins < cost) return
      this.coins -= cost
    }

    this.rerollCount++
    this.snapshotLockedOffers()
    this.refreshOffers()
    this.persistState(true)
    this.renderHangar()
    playConfirm(this)
  }

  private handleBuy(offer: ShopOffer): void {
    if (this.coins < offer.price) return

    this.coins -= offer.price
    this.player = applyUpgrade(this.player, offer.upgrade)
    if (!offer.upgrade.repeatable) {
      this.boughtIds.add(offer.upgrade.id)
    }

    this.boughtSlots.add(offer.slotId)
    this.persistState(true)

    const allBought = this.offers.every((o) => this.boughtSlots.has(o.slotId))
    if (allBought) {
      this.boughtSlots = new Set()
      this.snapshotLockedOffers()
      this.refreshOffers()
      this.rerollCount = 0
      this.renderHangar()
    } else {
      this.renderHangar()
    }
  }

  private finishShopping(): void {
    this.persistState(true)

    const waveRuntime = getWaveRuntime(this)
    if (waveRuntime) {
      waveRuntime.resumeAfterShop()
    } else {
      this.scene.stop(SCENE.SHOP)
      this.scene.start(SCENE.WAVE, { wave: this.wave })
    }
  }
}
